import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

type Tone = "neutral" | "live" | "draft" | "paused" | "danger" | "info";

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  children?: ReactNode;
}

const toneClasses: Record<Tone, string> = {
  neutral:
    "bg-[var(--color-graphite-600)] text-[var(--color-snow)]",
  live: "bg-[var(--color-graphite-600)] text-[var(--color-mint)]",
  draft:
    "bg-[var(--color-graphite-600)] text-[var(--color-slate-200)]",
  paused:
    "bg-[var(--color-graphite-600)] text-[var(--color-slate-300)]",
  danger: "bg-[var(--color-ember-dark)] text-[var(--color-ember)]",
  info: "bg-[var(--color-graphite-600)] text-[var(--color-sky)]",
};

export function Badge({ className, tone = "neutral", children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[var(--radius-badge)]",
        "font-mono text-[11px] tracking-[0.04em] font-medium uppercase",
        toneClasses[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export function StatusDot({ tone = "live", className }: { tone?: "live" | "ember" | "mint" | "sky"; className?: string }) {
  const colorClass =
    tone === "ember"
      ? "bg-[var(--color-ember)] anim-pulse-ember"
      : tone === "mint"
        ? "bg-[var(--color-mint)]"
        : tone === "sky"
          ? "bg-[var(--color-sky)]"
          : "bg-[var(--color-mint)] anim-pulse-ember";
  return (
    <span
      aria-hidden
      className={cn("inline-block h-2 w-2 rounded-full", colorClass, className)}
    />
  );
}
